import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

// MUI Icons
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Login as LoginIcon,
  MoreVert as MoreVertIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';

// Contexts
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

// Components
import CustomLinearProgress from '../../components/Loaders/CustomLinearProgress';

// Helpers
import { fetchWrapperNoAuth, SERVER, sha256 } from '../../utils/helpers';
import SentItemsModal from '../../components/Modals/SentItemsModal';

const SuperUserDashboard = () => {
  const navigate = useNavigate();
  const { userSU, passwordSU, subUsers, updateSubUser, removeSubUser, login, loginSU, updateSubUserBillingRate, subscribedSU } = useAuth();
  const { theme } = useTheme();

  const [modalOpen, setModalOpen] = useState(false);

  const [menuState, setMenuState] = useState({
    anchorEl: null,
    selectedUser: null
  });
  const actions = [
    { icon: <LoginIcon sx={{ color: theme.color.primary }} />, name: 'Login', onClick: (subUser) => handleLoginClick(subUser), color: theme.color.primary },
    { icon: <PreviewIcon sx={{ color: theme.color.primary }} />, name: 'Preview', onClick: (subUser) => handleSubUserPreview(subUser), color: theme.color.primary },
    { icon: <DeleteIcon sx={{ color: theme.accent.error }} />, name: 'Remove', onClick: (subUser) => handleDeleteSubUser(subUser.email), color: theme.accent.error }
  ];

  const [openDialog, setOpenDialog] = useState(false);
  const [currentSubUser, setCurrentSubUser] = useState(null);
  const [currentSubUserPreview, setCurrentSubUserPreview] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    business: '',
    name: ''
  });

  const totalUnitsAdded = (subUsers || []).reduce((sum, user) => {
    if (user.sent_items && Array.isArray(user.sent_items)) {
      return sum + (user.sent_items || []).reduce((userSum, item) => userSum + (parseInt(item.qty) || 0), 0);
    }
    return sum;
  }, 0);
  const totalBilling = subUsers.reduce((sum, user) => {
    if (user.sent_items && Array.isArray(user.sent_items)) {
      return sum + (user.sent_items || []).reduce((userSum, item) => userSum + ((item.unpaidQty || 0) * (user.billingRate || 0)), 0);
    }
    return sum;
  }, 0);

  const handleCloseModal = async () => {
    try {
      const body = {
        email: userSU,
        subUsers: subUsers
      }

      fetchWrapperNoAuth(`${SERVER}/falcon_save_sub_users`, userSU, passwordSU, { method: 'POST', body: body });
    } catch (error) {
      console.error("Error updating sub-user:", error);
    } finally {
      setModalOpen(false);
      setCurrentSubUser(null);
    }
  }

  // When opening the menu:
  const handleMenuOpen = (event, subUser) => {
    setMenuState({
      anchorEl: event.currentTarget,
      selectedUser: subUser
    });
  };

  // When closing the menu:
  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      selectedUser: null
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open dialog for adding new sub-user
  const handleAddNewClick = () => {
    setCurrentSubUser(null);
    setFormData({
      email: '',
      password: '',
      business: '',
      name: ''
    });
    setOpenDialog(true);
  };

  const handleLoadBatches = async (email) => {
    const body = {
      email: email,
      emailSU: userSU
    };
    try {
      const batches = await fetchWrapperNoAuth(`${SERVER}/falcon_load_batches_su`, userSU, passwordSU, { method: "POST", body: body });
      if (Object.keys(batches).length > 0) {
        localStorage.setItem("IFAddItemProccess", JSON.stringify(batches.AddItem));
        localStorage.setItem("IFAddItemsBulkProccess", JSON.stringify(batches.AddItemsBulk));
        localStorage.setItem("IFCsvUploadProccess", JSON.stringify(batches.CsvUpload));
        localStorage.setItem("IFCsvUploadsBulkProccess", JSON.stringify(batches.CsvUploadsBulk));
        localStorage.setItem("IFMarterBuyListProcess", JSON.stringify(batches.MarterBuyList));
        localStorage.setItem("IFMarterBuyListsBulkProcess", JSON.stringify(batches.MarterBuyListsBulk));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Open dialog for editing existing sub-user
  const handleLoginClick = async (subUser) => {
    setCurrentSubUser(subUser);
    if (userSU) {
      try {
        const result = await fetchWrapperNoAuth(`${SERVER}/falcon_get_sub_details_su/${userSU.toLowerCase()}/`, userSU, passwordSU);
        if (result.active) {
          const body = { email: subUser.email.toLowerCase(), password: subUser.password, }
          const response = await fetchWrapperNoAuth(`${SERVER}/falcon_start`, subUser.email.toLowerCase(), subUser.password, { method: 'POST', body: body });
          await handleLoadBatches(subUser.email.toLowerCase());
          await login(response);
          setCurrentSubUser(null);
          navigate('/');
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Handle sub-user save
  const handleSaveSubUser = async () => {
    const subUserData = {
      email: formData.email.toLowerCase(),
      password: await sha256(formData.password),
      business: formData.business,
      name: formData.name
    };

    const success = await updateSubUser(subUserData);
    if (success) {
      setOpenDialog(false);
    } else {
      // Handle error
      alert('Failed to save sub-user. Please try again.');
    }
  };

  // Handle sub-user delete
  const handleDeleteSubUser = async (email) => {
    if (window.confirm('Are you sure you want to remove this sub-user?')) {
      const success = await removeSubUser(email);
      if (!success) {
        // Handle error
        alert('Failed to remove sub-user. Please try again.');
      }
    }
  };

  // Handle sub-user preview
  const handleSubUserPreview = (subUser) => {
    setCurrentSubUserPreview(subUser);
    setModalOpen(true);
  };

  const styles = {
    input: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: theme.color.primary,
        },
        "&:hover fieldset": {
          borderColor: theme.color.secondary,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.color.primary,
        },
      },
      "& .MuiInputLabel-root": {
        color: theme.text.tertiary,
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: theme.color.primary,
        },
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
      "& .MuiInputLabel-outlined.Mui-error": {
        color: theme.accent.error,
      },
      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.accent.error,
      },
    },
    dialog: {
      '& .MuiPaper-root': {
        backgroundColor: theme.background.primary,
        border: `2px solid ${theme.border.light}`
      }
    },
    containedButton: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      backgroundColor: theme.color.primary,
      border: `1px solid ${theme.border.light}`,
    },
    outlinedButton: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.color.primary,
      borderRadius: '20px',
      backgroundColor: 'transparent',
      border: `0px solid ${theme.border.dark}`,
    },
    td: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary
    },
    tdClickable: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      textDecoration: 'underline',
      cursor: 'pointer',
      display: "inline-block"
    },
    tableInput: {
      width: '100px',
      '& .MuiInput-underline:before': { // Default bottom border
        borderBottomColor: theme.border.dark,
      },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { // Hover state
        borderBottomColor: theme.border.medium,
      },
      '& .MuiInput-underline:after': { // Focused state
        borderBottomColor: theme.color.secondary,
      },
    },
    card: {
      mb: 3,
      p: 2,
      backgroundColor: `${theme.background.secondary}80`,
      cursor: 'pointer',
      borderRadius: '15px'
    }
  }

  useEffect(() => {
    const loadSuperUser = async () => {
      const body = { email: userSU, password: passwordSU }

      try {
        const response = await fetchWrapperNoAuth(`${SERVER}/falcon_start_su`, userSU, passwordSU, { method: 'POST', body: body });
        await loginSU(response);
      } catch (e) { }
    }
    loadSuperUser();
  }, []);

  if (!userSU) {
    return <Navigate to="/login-super-user" replace />;
  }

  // if (!subscribedSU) {
  //   // Redirect to pricing if not subscribed
  //   return <Navigate to="/pricing-super-user" replace />;
  // }

  return (
    <Box sx={{ px: 3, backgroundColor: theme.background.primary, minHeight: '-webkit-fill-available', }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
          Super User Dashboard
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <Card sx={styles.card}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.primary, textAlign: 'center', }}>
              Sub-Users
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, textAlign: 'center', }}>
              {subUsers.length || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.card}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.primary, textAlign: 'center', }}>
              Units Added
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, textAlign: 'center', }}>
              {totalUnitsAdded}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.card}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.primary, textAlign: 'center', }}>
              Total billing
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, textAlign: 'center', }}>
              ${totalBilling.toFixed(2)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={styles.card} onClick={handleAddNewClick}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1, color: theme.text.primary, textAlign: 'center', }}>
              Add Sub-User
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, textAlign: 'center', }}>
              <IconButton>
                <AddIcon sx={{ color: theme.text.primary }} />
              </IconButton>
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Typography variant="h5" sx={{ fontFamily: theme.fontFamily, mb: 2, color: theme.text.primary }}>
        Managed Sub-Users
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.color.secondary }}>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Email</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Name</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Business Name</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Units Added</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Un-Paid Units</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Billing Rate</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Total Billing</TableCell>
              <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.negative }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subUsers && subUsers.length > 0 ? (
              subUsers.map((subUser) => {
                if (JSON.stringify(subUser) === JSON.stringify(currentSubUser)) {
                  return (
                    <TableRow key={subUser.email} sx={{ backgroundColor: theme.background.secondary }}>
                      <TableCell sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }} colSpan={8}>
                        <CustomLinearProgress />
                      </TableCell>
                    </TableRow>
                  )
                } else {
                  return (
                    <TableRow key={subUser.email} sx={{ backgroundColor: theme.background.secondary }}>
                      <TableCell sx={styles.td}>{subUser.email}</TableCell>
                      <TableCell sx={styles.td}>{subUser.name}</TableCell>
                      <TableCell sx={styles.td}>{subUser.business}</TableCell>
                      <TableCell>
                        <Typography sx={styles.tdClickable} onClick={() => handleSubUserPreview(subUser)}>{(subUser.sent_items || [])?.reduce((acc, item) => acc + (parseInt(item?.qty) || 0), 0) || 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={styles.tdClickable} onClick={() => handleSubUserPreview(subUser)}>{(subUser.sent_items || [])?.reduce((acc, item) => acc + (parseInt(item?.unpaidQty) || 0), 0) || 0}</Typography>
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <TextField
                          type="number"
                          size="small"
                          variant="standard"
                          value={subUser.billingRate || 0}
                          onChange={(e) => {
                            const newRate = parseFloat(e.target.value) || 0;
                            updateSubUserBillingRate(subUser.email, newRate);
                          }}
                          onBlur={async () => {
                            try {
                              const body = {
                                email: userSU,
                                subUsers: subUsers
                              }

                              fetchWrapperNoAuth(`${SERVER}/falcon_save_sub_users`, userSU, passwordSU, { method: 'POST', body: body });
                            } catch (error) {
                              console.error("Error updating sub-user:", error);
                            } finally {
                              setModalOpen(false);
                              setCurrentSubUser(null);
                            }
                          }}
                          sx={styles.tableInput}
                          slotProps={{
                            input: {
                              sx: {
                                fontFamily: theme.fontFamily,
                                color: theme.text.primary,
                                padding: '4px 8px',
                              },
                            },
                            htmlInput: {
                              step: 0.01,
                              min: 0,
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={styles.td}>${((subUser.sent_items || [])?.reduce((acc, item) => acc + ((parseInt(item?.unpaidQty) || 0) * (subUser.billingRate || 0)), 0) || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Box>
                          <IconButton
                            aria-label="more options"
                            onClick={(event) => handleMenuOpen(event, subUser)}
                          >
                            <MoreVertIcon sx={{ color: theme.text.primary }} />
                          </IconButton>
                          <Menu
                            anchorEl={menuState.anchorEl}
                            open={Boolean(menuState.anchorEl)}
                            onClose={handleMenuClose}
                            slotProps={{
                              paper: {
                                sx: {
                                  backgroundColor: theme.background.secondary,
                                  border: `1px solid ${theme.border.light}`
                                },
                              }
                            }}
                          >
                            {actions.map((action) => (
                              <MenuItem
                                key={action.name}
                                onClick={() => {
                                  action.onClick(menuState.selectedUser);
                                  handleMenuClose();
                                }}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: theme.background.primary
                                  }
                                }}
                              >
                                {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                                <ListItemText primary={<Typography sx={{ fontFamily: theme.fontFamily, color: action.color, }}>{action.name}</Typography>} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                }
              }
              )
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ fontFamily: theme.fontFamily }}>
                  No sub-users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for adding/editing sub-users */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm" sx={styles.dialog}>
        <DialogTitle sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
          {currentSubUser ? 'Edit Sub-User' : 'Add New Sub-User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={!!currentSubUser} // Disable email field when editing
              fullWidth
              required
              sx={styles.input}
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              sx={styles.input}
            />
            <TextField
              label="Business Name"
              name="business"
              value={formData.business}
              onChange={handleInputChange}
              fullWidth
              sx={styles.input}
            />
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              sx={styles.input}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={styles.outlinedButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSubUser}
            variant="contained"
            sx={styles.containedButton}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <SentItemsModal
        open={modalOpen}
        handleClose={handleCloseModal}
        userData={currentSubUserPreview}
        setUserData={setCurrentSubUserPreview}
      />
    </Box>
  );
};

export default SuperUserDashboard;